'use strict';

export type JistiMeetJSt = any;

export type Perlin = any;
declare const Simple1DNoise: Perlin;
export const perlin: Perlin = new Simple1DNoise();

import {
  GameManager,
  Time,
  DeltaTime,
  PlaygroundDOM,
  type Space,
  RenderItemType,
  type TextObject,
  type TextButton,
} from './types';
import {
  getZeroDebugState,
  v2,
  checkIfMobile,
  getCanvas2DContext,
  getTimezoneName,
} from './helpers';
export { thnkBasePath, pgPath, pgBasePath, VERSION_CLIENT, convertUrl } from './config';
export { PG_SERVER_HOST, API_HOST } from './config';
export { getZeroGameManager } from './game_manager';
import { getZeroGameManager } from './game_manager';

import { simulate, updateDOM } from './simulate';
import {
  setButtonState,
  buttonStates,
  onKeyUp,
  onKeyDown,
  handleTouchStart,
  handleTouchEnd,
  handleTouchMove,
} from './input';

import {
  addEntity,
  getZeroEntity,
  getZeroStaticEntity,
  setEntityPosition,
  getObjectsByType,
} from './entity';
import * as DataController from './controllers/data_controller';
import './dropdown';

import { recalculateWordSizes } from './textobject/textobject';
import { canvasZoomIn, canvasZoomOut, setDefaultZoom } from './canvas';
import { getZeroCursor, initCursor } from './cursor';
import * as Background from './background_controller';
import { ObjectType } from './shared/api_shared_types';

import { requestCloseRoom } from './room';
import { imageListToLoad } from './imageList';
import { getGameControllerMap } from './gameobject';
import { initSpace } from './space';
import Hls from 'hls.js';
import logger from './controllers/logger';
import * as Localization from './localizationController';
import * as WichCollectorPT1 from './wishcollector_prototype1';

import {
  createDebugPackage,
  createDevInfoPackage,
  createRenderPackageFromHierarchy,
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageClearRect,
  type RenderPackageItem,
} from './render_package';

import { GRADIENTS } from './constants';
import * as LocalSettingsController from './localSettingsController';
import * as PerformanceController from './performance_controller';

/*
 *  globals
 */
export let dt: DeltaTime = 0.016;
export let time: Time = 0;
export let frameNr: number = 0;
export { lastHideTime } from './window_layout';

//needs rewrite, datacontroller should manager async font-loading callback from worker
let fontsLoadedByWorker = 0;

export const debugState = getZeroDebugState();

// const params = new URL(document.location.toString()).searchParams;

const worker = new Worker(new URL('./canvas-worker.ts', import.meta.url));

if (worker) {
  worker.onmessage = (event) => {
    if (event.data.type === 'resize-complete') {
      if (gameManager.DOM.backCanvasElement) {
        gameManager.DOM.backCanvasElement.style.width = window.innerWidth + 'px';
        gameManager.DOM.backCanvasElement.style.height = window.innerHeight + 'px';
      }
      gameManager.DOM.canvasElement.style.width = window.innerWidth + 'px';
      gameManager.DOM.canvasElement.style.height = window.innerHeight + 'px';
    } else if (event.data.type === 'fonts-loaded') {
      //needs rewrite, datacontroller should manager async font-loading callback from worker
      fontsLoadedByWorker = event.data.count;
      if (fontsLoadedByWorker === 3) {
        onAllFontsLoaded();
      }
    }
  };
}

const Ctx_0_Base_Scale: number = Number.parseFloat(process.env.THNK_CTX0_SCALE) || 0.5;
const Ctx_1_Base_Scale: number = Number.parseFloat(process.env.THNK_CTX1_SCALE) || 1;
const Ctx_1_Base_Scale_Tier: [number, number, number] = [
  Ctx_1_Base_Scale,
  Number.parseFloat(process.env.THNK_CTX1_SCALE_TIER1) || Ctx_1_Base_Scale,
  Number.parseFloat(process.env.THNK_CTX1_SCALE_TIER2) || 0.75,
];
const disable_background_animation =
  process.env.THNK_DISABLE_BACKGROUND_ANIMATION &&
  process.env.THNK_DISABLE_BACKGROUND_ANIMATION === 'true';

const pixelRatio = window.devicePixelRatio;
let Ctx_0_Scale = pixelRatio * Ctx_0_Base_Scale;
let Ctx_1_Scale = pixelRatio * Ctx_1_Base_Scale;

let offscreen: OffscreenCanvas = null;
let offscreenBG: OffscreenCanvas = null;

let prevTime = 0;

if (!checkIfMobile()) {
  const mobileWarning = document.querySelector('#mobile-warning');
  if (mobileWarning) mobileWarning.remove();
}

export const gameManager = getZeroGameManager();
gameManager.isMobile = checkIfMobile();
gameManager.localTimeZone = getTimezoneName();
DataController.init(worker);

gameManager.objectControllerMap = getGameControllerMap();

if (process.env.THNK_PAGE_TITLE) {
  gameManager.pageTitle = process.env.THNK_PAGE_TITLE;
}

gameManager.DOM = initPlaygroundDOM();

gameManager.space = initSpace(gameManager, getZeroStaticEntity());
gameManager.spaceBG = initSpace(gameManager, getZeroStaticEntity());

const setViewportUnits = () => {
  document.body.style.setProperty('--vh', `${(window.innerHeight / 100).toFixed(2)}px`);
  document.body.style.setProperty('--vw', `${(window.innerWidth / 100).toFixed(2)}px`);
};
setViewportUnits();

await LocalSettingsController.init(gameManager);

async function setup() {
  const urlLang = new URLSearchParams(window.location.search).get('lang') || undefined;
  await Localization.init(urlLang);
  Localization.applyDomTranslations();

  const fontsRethink = [
    {
      fontId: 'font_regular',
      fontUri: 'fonts/Roboto-Medium.ttf',
    },
    {
      fontId: 'font_bold',
      fontUri: 'fonts/Roboto-Medium.ttf',
    },
    {
      fontId: 'font_extrabold',
      fontUri: 'fonts/Roboto-Medium.ttf',
    },
  ];
  const fonts = fontsRethink;
  DataController.loadFonts(fonts, onAllFontsLoaded);

  if (worker) {
    DataController.loadAndSendFontToWorker(worker, 'font_regular', 'fonts/Roboto-Medium.ttf');
    DataController.loadAndSendFontToWorker(worker, 'font_bold', 'fonts/Roboto-Medium.ttf');
    DataController.loadAndSendFontToWorker(
      worker,
      'font_extrabold',
      'fonts/Roboto-Medium.ttf',
    );
  }

  if (!disable_background_animation) {
    gameManager.DOM.backCanvasElement = document.createElement('canvas');
    gameManager.DOM.backCanvasElement.width = window.innerWidth * Ctx_1_Scale;
    gameManager.DOM.backCanvasElement.height = window.innerHeight * Ctx_1_Scale;
    const parent = document.querySelector('#canvas-layer-0') as HTMLDivElement;
    parent.appendChild(gameManager.DOM.backCanvasElement);
    offscreenBG = gameManager.DOM.backCanvasElement.transferControlToOffscreen();
  }

  gameManager.DOM.canvasElement = document.createElement('canvas');
  gameManager.DOM.canvasElement.width = window.innerWidth * Ctx_1_Scale;
  gameManager.DOM.canvasElement.height = window.innerHeight * Ctx_1_Scale;
  const parent = document.querySelector('#canvas-layer-1') as HTMLDivElement;
  parent.appendChild(gameManager.DOM.canvasElement);
  offscreen = gameManager.DOM.canvasElement.transferControlToOffscreen();

  checkWindowSize();

  DataController.loadImageListInDataController(imageListToLoad);

  DataController.addSpriteSheet('spritesheet_soft_white', 'spritesheet_soft_white', 2, 2);

  const loader = document.querySelector('#loader');
  loader.remove();

  if (!Hls.isSupported()) {
    logger.error('Cannot use HLS, videos cannot be played.');
  }

  gameManager.textRenderCtx = createTextRenderContext();

  resetMouseCursor();

  //Create new cursor object
  //@TODO: Make function in entity or simulate, to avoid all these needed imports
  {
    gameManager.cursor = getZeroCursor(getZeroEntity());
    addEntity(gameManager, gameManager.cursor);
    initCursor(gameManager.cursor, gameManager);
    setEntityPosition(gameManager.cursor, window.innerWidth / 2, window.innerHeight / 2);
  }

  Background.initBackground(gameManager, 'images/background.jpg');

  bindGenericEvents();
  bindBodyEvents();
  bindCanvasEvents(gameManager.DOM.canvasElement);

  if (worker) {
    const offscreenCanvasArray: Array<Transferable | OffscreenCanvas> = [offscreen];
    let workerMessage: any = {
      type: 'init',
      canvas: offscreen,
      width: window.innerWidth * Ctx_1_Scale,
      height: window.innerHeight * Ctx_1_Scale,
    };

    if (offscreenBG) {
      workerMessage.canvasBG = offscreenBG;
      workerMessage.widthBG = window.innerWidth * Ctx_0_Scale;
      workerMessage.heightBG = window.innerHeight * Ctx_0_Scale;
      offscreenCanvasArray.push(offscreenBG);
    }

    worker.postMessage(workerMessage, offscreenCanvasArray);

    worker.postMessage({
      type: 'load-gradients',
      gradientData: GRADIENTS,
    });
  }

  WichCollectorPT1.init(gameManager);

  //Start update loop
  window.requestAnimationFrame(update);
}

const DEBUG_DT = 0;

function update(timestamp: number) {
  if (debugState.pauseUpdate) return;

  time = timestamp / 1000;
  frameNr++;

  // if (time > 5) {
  // debugState.pauseUpdate = true;
  // }

  if (DEBUG_DT && time - prevTime < DEBUG_DT) {
    window.requestAnimationFrame(update);
    return;
  }

  dt = time - prevTime;
  if (dt > 0.5) {
    dt = 0.5;
  }

  prevTime = time;

  PerformanceController.update(gameManager, dt);

  if (gameManager.performanceTierChanged) {
    gameManager.performanceTierChanged = false;
    Ctx_1_Scale = pixelRatio * Ctx_1_Base_Scale_Tier[gameManager.performanceTier];
    applyWindowResized(gameManager.space);
    applyWindowResized(gameManager.spaceBG);
  }

  const notificationDisplay = gameManager.DOM.connectionNotification.style.display;

  if (!gameManager.space.resizeApplied && time - gameManager.space.lastWindowResizedTime > 0.2) {
    applyWindowResized(gameManager.space);
    applyWindowResized(gameManager.spaceBG);
  }

  const spaceChanged = gameManager.space.layoutChanged;

  let viewHasChanged = false;
  let doRedraw = false;
  try {
    viewHasChanged = simulate(gameManager);

    doRedraw = debugState.alwaysRedraw || viewHasChanged;

    updateDOM(gameManager);
  } catch (e) {
    logger.error(e);
    return;
  }

  //background
  let bgRenderPackage: RenderPackageItem[] = null;
  if (!disable_background_animation && (!Background.isPaused() || doRedraw)) {
    const width = gameManager.DOM.backCanvasElement.width / Ctx_0_Scale;
    const height = gameManager.DOM.backCanvasElement.height / Ctx_0_Scale;
    const cRect: RenderPackageClearRect = {
      type: RenderItemType.clearRect,
      ...EMPTY_RENDER_TRANSFORM,
      scale: Ctx_0_Scale,
      positionX: 0,
      positionY: 0,
      width,
      height,
      roundedCorners: false,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
    };
    bgRenderPackage = [cRect];
  }
  if (!disable_background_animation && (!Background.isPaused() || doRedraw)) {
    bgRenderPackage.push(
      ...createRenderPackageFromHierarchy(gameManager.spaceBG, v2(), 0, Ctx_0_Scale, 1),
    );
  }

  //canvas
  let renderPackage: RenderPackageItem[] = null;

  if (debugState.showEntityCounts) {
    doRedraw = true;
  }

  if (doRedraw) {
    const width = gameManager.DOM.canvasElement.width / Ctx_1_Scale;
    const height = gameManager.DOM.canvasElement.height / Ctx_1_Scale;
    const cRect: RenderPackageClearRect = {
      type: RenderItemType.clearRect,
      ...EMPTY_RENDER_TRANSFORM,
      scale: Ctx_1_Scale,
      positionX: 0,
      positionY: 0,
      width,
      height,
      roundedCorners: false,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
    };

    renderPackage = [cRect];

    renderPackage.push(
      ...createRenderPackageFromHierarchy(gameManager.space, v2(), 0, Ctx_1_Scale, 1),
    );
  }

  //debug info
  if (debugState.isDebugMode) {
    let devInfoView = createDevInfoPackage(gameManager, doRedraw, Ctx_1_Scale);
    if (!renderPackage) renderPackage = [];
    renderPackage.push(...devInfoView);
  }
  if (debugState.showEntityCounts) {
    let devInfoView = createDebugPackage(gameManager, Ctx_1_Scale);
    if (!renderPackage) renderPackage = [];
    renderPackage.push(...devInfoView);
  }
  if ((renderPackage && renderPackage.length) || (bgRenderPackage && bgRenderPackage.length)) {
    worker.postMessage({ type: 'render', frameNr, renderPackage, bgRenderPackage });
  }

  if (spaceChanged) {
    //todo: move to space update
    gameManager.space.layoutChanged = false;
  }

  window.requestAnimationFrame(update);
}

function windowResized() {
  gameManager.space.lastWindowResizedTime = time;
  gameManager.space.resizeApplied = false;
  gameManager.space.isDirty = true;
}

//This resize is called after a short delay, so not every frame when the
//user is resizing the window.
function applyWindowResized(space: Space) {
  space.requestResize = true;

  checkWindowSize();

  if (worker) {
    worker.postMessage({
      type: 'resize',
      width: window.innerWidth * Ctx_1_Scale,
      height: window.innerHeight * Ctx_1_Scale,
      widthBG: window.innerWidth * Ctx_0_Scale,
      heightBG: window.innerHeight * Ctx_0_Scale,
      // scale: Ctx_1_Scale,
    });
  }

  gameManager.space.resizeApplied = true;
}

function checkWindowSize() {
  let isFontResized = false;

  if (window.innerWidth < 1440 || window.innerHeight < 680) {
    if (gameManager.rootFontSizePx !== 16) {
      gameManager.rootFontSizePx = 16;
      gameManager.rootUnitSizePx = 75;
      isFontResized = true;
    }
  } else {
    if (gameManager.rootFontSizePx !== 20) {
      gameManager.rootFontSizePx = 20;
      gameManager.rootUnitSizePx = 100;
      isFontResized = true;
    }
  }

  if (isFontResized) {
    for (const tObj of getObjectsByType<TextObject>(gameManager, ObjectType.TEXTOBJECT)) {
      recalculateWordSizes(tObj);
    }
  }
}

function onAllFontsLoaded() {
  //needs rewrite, datacontroller should manager async font-loading callback from worker
  if (fontsLoadedByWorker === 3) {
    for (const tObj of getObjectsByType<TextObject>(gameManager, ObjectType.TEXTOBJECT)) {
      recalculateWordSizes(tObj);
    }
  }
}

export { resetMouseCursor } from './window_layout';
import { onBlur, onFocus, onVisibilityChanged, resetMouseCursor } from './window_layout';

export function showMouseCursor() {
  gameManager.DOM.canvasElement.style.cursor = 'default';
}

if (process.env.THNK_PLAYGROUND_DEBUG_MODE) {
  debugState.isDebugMode = JSON.parse(process.env.THNK_PLAYGROUND_DEBUG_MODE);
}

import { mouseOut, mouseOver, scroll, onClipboardPaste, handleFileDrop } from './input_events';

export { showBlurLayer, hideBlurLayer } from './blur_layer';

function ShowMobileWarning() {
  const loader = document.querySelector('#loader');
  loader.remove();
  const mobileWarning = document.querySelector('#mobile-warning') as HTMLElement;
  mobileWarning.style.display = '';
}

export function setPreventScroll(preventScroll: boolean) {
  gameManager.preventScroll = preventScroll;
}

export function setPlaygroundPaused(paused: boolean) {
  gameManager.paused = paused;
}

function initPlaygroundDOM(): PlaygroundDOM {
  return {
    ctxBackground: null,
    ctxMain: null,
    backCanvasElement: null,
    canvasElement: null,

    blurContainer: document.querySelector('#blur-container'),
    videoContainer: document.querySelector('#video-container'),
    audioContainer: document.querySelector('#audio-container'),
    overlayContainer: document.querySelector('#overlay-container'),
    backgroundContainer: document.querySelector('#background-container'),
    backgroundVideoContainer: document.querySelector('#background-video-container'),
    blurLayer: document.querySelector('#blur-layer'),
    connectionNotification: document.querySelector('#connection-notification'),
    connectionNotificationMessage: document.querySelector('#reconnect-loader-msg'),
    cursorCircle: null,
  };
}

function createTextRenderContext(): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
  let textRenderCtx;

  const oc = new OffscreenCanvas(1, 1);
  if (!oc) {
    console.warn('Offscreencanvas is not available, fallback to canvas');
    textRenderCtx = getCanvas2DContext(1, 1);
  } else {
    const ctx = oc.getContext('2d');
    if (!ctx) {
      console.warn('Offscreencanvas is not available, fallback to canvas');
      textRenderCtx = getCanvas2DContext(1, 1);
    } else {
      textRenderCtx = ctx;
    }
  }

  return textRenderCtx;
}
function bindCanvasEvents(canvasElement: HTMLCanvasElement) {
  canvasElement.addEventListener('mouseout', (e) => mouseOut(gameManager, e));
  canvasElement.addEventListener('mouseover', () => mouseOver(gameManager));

  canvasElement.addEventListener('click', (event: MouseEvent) => {
    //To support Safari, which needs to trigger open link actions from a direct user interation

    for (const textButton of getObjectsByType<TextButton>(gameManager, ObjectType.TEXT_BUTTON)) {
      if (textButton.onClick) {
        textButton.onClick(event);
      }
    }
  });

  canvasElement.addEventListener('mousedown', (event: MouseEvent) => {
    if (event.button === 0) {
      if (gameManager.paused || gameManager.cursor.enabled) return;
      setButtonState(buttonStates.LEFT_MOUSE, true);
    }
  });

  document.addEventListener('mouseup', (event: MouseEvent) => {
    if (event.button === 0 && !gameManager.paused) {
      setButtonState(buttonStates.LEFT_MOUSE, false);
    }
  });

  canvasElement.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      handleTouchMove(gameManager, e);
    },
    { passive: false },
  );

  canvasElement.addEventListener('touchstart', (e: TouchEvent) => {
    handleTouchStart(gameManager, e);
  });

  canvasElement.addEventListener('touchend', (e: TouchEvent) => {
    handleTouchEnd(gameManager, e);
  });

  document.addEventListener('pointermove', (e) => {
    gameManager.mousePos.x = e.clientX;
    gameManager.mousePos.y = e.clientY;
    // window.requestAnimationFrame(update);
  });

  gameManager.DOM.canvasElement.addEventListener('dragover', (ev) => {
    ev.preventDefault();
  });

  gameManager.DOM.canvasElement.addEventListener('drop', (ev: DragEvent) => {
    ev.preventDefault();

    handleFileDrop(ev, gameManager);
  });
}

function bindBodyEvents() {
  let element = document.querySelector('body');
  //full screen logic
  //note: when user does manual full-screen, this cannot be detected and
  //is an override of the scripted full-screen logic
}

function bindGenericEvents() {
  document.addEventListener('paste', (e) => onClipboardPaste(e, gameManager));
  window.addEventListener('blur', onBlur);
  window.addEventListener('focus', () => onFocus(gameManager));

  document.addEventListener('visibilitychange', onVisibilityChanged);

  window.addEventListener(
    'wheel',
    (e) => {
      scroll(e, gameManager);
    },
    { passive: false },
  );
  window.addEventListener('resize', () => {
    setViewportUnits();
    windowResized();
  });
  window.addEventListener('keydown', (event: KeyboardEvent) => {
    if (!gameManager.paused) {
      onKeyDown(event, time);
    }
  });
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('contextmenu', (event: MouseEvent) => {
    event.preventDefault();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      // This page was restored from the bfcache,
      // the socket is probably disconnected but the client does not know this yet
      // Let's do a hard reconnect
    } else {
      // This page was loaded normally
    }
  });
}

setup();
